import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import Step2 from "./Step2.jsx";
import Step3 from "./Step3.jsx";

const propTypes = {
  howDidYouHearItems: PropTypes.array,
  countries: PropTypes.array,
  states: PropTypes.array,
  googleSiteKey: PropTypes.string,
  underwriterOptions: PropTypes.array,
  dataSourceId: PropTypes.string,
};

const RegistrationForm = props => {
  const {
    howDidYouHearItems,
    countries,
    states,
    googleSiteKey,
    underwriterOptions,
    dataSourceId,
  } = props;

  // ----------------------------
  // Field state
  // ----------------------------
  const [email, setEmail] = useState("");
  const [agencyId, setAgencyId] = useState("");
  const [underwriter, setUnderwriter] = useState("");
  const [companyAgencyId, setCompanyAgencyId] = useState("");
  const [companyUnderwriter, setCompanyUnderwriter] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");

  const [verificationData, setVerificationData] = useState(null);
  const [selectedHowDidYouHear, setSelectedHowDidYouHear] = useState([]);
  const [howDidYouHearOther, setHowDidYouHearOther] = useState("");
  const [marketingContactName, setMarketingContactName] = useState("");
  const [marketingContactPhone, setMarketingContactPhone] = useState("");
  const [marketingContactEmail, setMarketingContactEmail] = useState("");

  // ----------------------------
  // Flow flags
  // ----------------------------
  const [stepOneVerified, setStepOneVerified] = useState(false);
  const [stepOneCompleted, setStepOneCompleted] = useState(false);
  const [isStewartEmployee, setIsStewartEmployee] = useState(false);

  const [isVerifying, setIsVerifying] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // ----------------------------
  // Messages & errors
  // ----------------------------
  const [errors, setErrors] = useState({});
  const [stepOneError, setStepOneError] = useState("");
  const [submitError, setSubmitError] = useState("");
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [announcement, setAnnouncement] = useState("");

  // ----------------------------
  // Script: reCAPTCHA
  // ----------------------------
  useEffect(() => {
    if (!googleSiteKey) return;
    const already =
      window.grecaptcha ||
      document.querySelector('script[src*="recaptcha/api.js"]');
    if (already) return;
    const script = document.createElement("script");
    script.src =
      "https://www.google.com/recaptcha/api.js?render=" + googleSiteKey;
    script.async = true;
    document.head.appendChild(script);
  }, [googleSiteKey]);

  // ----------------------------
  // Validation (centralized)
  // ----------------------------
  const req = v =>
    !v || !v.toString().trim() ? "Please fill out this field." : "";
  const sel = (v, label) => (!v ? `Please select ${label}.` : "");

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const emailVal = v => {
    const r = req(v);
    if (r) return r;
    if (!v.includes("@"))
      return `Please include an '@' in the email address. '${v}' is missing an '@'.`;
    if (!emailRegex.test(v))
      return `Please include a valid email address. '${v}' is not a valid format.`;
    return "";
  };
  const pwVal = v => {
    const r = req(v);
    if (r) return r;
    if (v.length < 6) return "Password must be at least 6 characters long.";
    const kinds = [
      /[A-Z]/,
      /[a-z]/,
      /[0-9]/,
      /[!@#$%^&*()\-_=+\[\]|;:,.{}]/,
    ].reduce((n, rx) => n + (rx.test(v) ? 1 : 0), 0);
    if (kinds < 3) {
      return "The password must be at least 6 characters and have 3 of: Uppercase, Lowercase, Numerals, Punctuation.";
    }
    return "";
  };

  const fieldIdFor = key =>
    ({
      email: "email",
      agency_id: "agency-id",
      underwriter: "underwriter",
      first_name: "first-name",
      last_name: "last-name",
      password: "password",
      confirm_password: "confirm-password",
      job_title: "job-title",
      country: "country",
      phone: "phone",
      zip: "zip",
    })[key] || key;

  const scrollToError = id => {
    const el = document.getElementById(id);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    el.focus({ preventScroll: true });
  };

  // validators map: keys match input name=
  const validators = {
    email: v => emailVal(v),
    agency_id: v => req(v),
    underwriter: v => sel(v, "an underwriter"),
    first_name: v => req(v),
    last_name: v => req(v),
    password: v => pwVal(v),
    confirm_password: (v, all) =>
      req(v) || (v !== all.password ? "Passwords do not match." : ""),
    job_title: v => req(v),
    country: v => sel(v, "a country"),
    addressstate: (v, all) => (all.country === "US" ? sel(v, "a state") : ""),
    phone: v => req(v),
    zip: (v, all) => (all.country === "US" ? req(v) : ""),
  };

  const formToObject = formEl => {
    const fd = new FormData(formEl);
    const obj = {};
    fd.forEach((val, key) => {
      obj[key] = val;
    });
    return obj;
  };

  const validateForm = (formEl, skipFields = []) => {
    const values = formToObject(formEl);
    const nextErrors = {};
    Object.keys(validators).forEach(name => {
      if (skipFields.includes(name)) return; // Skip validation for specified fields
      const fn = validators[name];
      const msg = fn ? fn(values[name], values) : "";
      if (msg) nextErrors[name] = msg;
    });
    return { values, errors: nextErrors };
  };

  const clearFieldError = key => setErrors(p => ({ ...p, [key]: "" }));

  const announceStepChange = stepMessage => {
    setAnnouncement(stepMessage);
    setTimeout(() => setAnnouncement(""), 1000);
  };

  // ----------------------------
  // Change handlers (clear errors inline)
  // ----------------------------
  const handleFirstNameChange = e => {
    setFirstName(e.target.value);
    clearFieldError("first_name");
  };
  const handleLastNameChange = e => {
    setLastName(e.target.value);
    clearFieldError("last_name");
  };
  const handleEmailChange = e => {
    setEmail(e.target.value);
    clearFieldError("email");
    if (stepOneError) setStepOneError("");
  };
  const handleAgencyIdChange = e => {
    setAgencyId(e.target.value);
    clearFieldError("agency_id");
  };
  const handleUnderwriterChange = e => {
    setUnderwriter(e.target.value);
    clearFieldError("underwriter");
  };

  const handleCompanyAgencyIdChange = e => setCompanyAgencyId(e.target.value);
  const handleCompanyUnderwriterChange = e =>
    setCompanyUnderwriter(e.target.value);

  const handleMarketingContactNameChange = e =>
    setMarketingContactName(e.target.value);
  const handleMarketingContactPhoneChange = e =>
    setMarketingContactPhone(e.target.value);
  const handleMarketingContactEmailChange = e =>
    setMarketingContactEmail(e.target.value);

  const handleHowDidYouHearChange = (value, checked) => {
    if (checked) setSelectedHowDidYouHear(prev => [...prev, value]);
    else setSelectedHowDidYouHear(prev => prev.filter(v => v !== value));
  };
  const handleHowDidYouHearOtherChange = e =>
    setHowDidYouHearOther(e.target.value);

  const handleEmailKeyDown = e => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (!stepOneVerified) handleVerifyAccount(e);
    }
  };

  // ----------------------------
  // Step 1: verify (email)
  // ----------------------------
  const handleVerifyAccount = async e => {
    e.preventDefault();

    const emailErr = validators.email(email);
    if (emailErr) {
      setErrors(p => ({ ...p, email: emailErr }));
      scrollToError("email");
      return;
    }
    setErrors(p => ({ ...p, email: "" }));
    setStepOneError("");
    setIsVerifying(true);

    try {
      const response = await fetch("/api/Account/VerifyUser", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          const userType = result.result?.userType;
          setVerificationData(result.result);

          // Clear all errors on successful verification
          setErrors({});
          setStepOneError("");

          if (userType === "stewartemployees") {
            setIsStewartEmployee(true);
            setStepOneVerified(true);
            setStepOneCompleted(true);
            announceStepChange(
              "Step 1 complete. Now showing Step 2: Account Information and Step 3: Company Information",
            );
          } else if (!userType || userType === "") {
            setIsStewartEmployee(false);
            setStepOneVerified(true);
            announceStepChange(
              "Email verified. Please complete your Agency ID and Underwriter information to continue.",
            );
          } else {
            // agents-paid or other types -> skip to steps 2/3
            setIsStewartEmployee(false);
            setStepOneVerified(true);
            setStepOneCompleted(true);
            announceStepChange(
              "Step 1 complete. Now showing Step 2: Account Information and Step 3: Company Information",
            );
          }
        } else {
          setStepOneVerified(false);
          setStepOneError(
            result.errorMessage ||
              "We could not verify your information. Please contact webmaster@stewartmarketing.com",
          );
        }
      } else {
        const errorData = await response.json().catch(() => ({}));
        setStepOneVerified(false);
        setStepOneError(
          errorData.message ||
            "We could not verify your information. Please contact webmaster@stewartmarketing.com",
        );
      }
    } catch (err) {
      console.error("Verification error:", err);
      setStepOneError(
        "An error occurred during verification. Please try again.",
      );
      setStepOneVerified(false);
    } finally {
      setIsVerifying(false);
    }
  };

  // ----------------------------
  // Step 1: complete (agency + underwriter)
  // ----------------------------
  const handleCompleteStepOne = async e => {
    e.preventDefault();

    const agencyErr = validators.agency_id(agencyId);
    const uwErr = validators.underwriter(underwriter);

    if (agencyErr || uwErr) {
      const next = {};
      if (agencyErr) next.agency_id = agencyErr;
      if (uwErr) next.underwriter = uwErr;
      setErrors(p => ({ ...p, ...next }));
      scrollToError(agencyErr ? "agency-id" : "underwriter");
      return;
    }

    setIsProcessing(true);
    try {
      const response = await fetch("/api/Account/VerifyUser", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, agencyId, underwriter }),
      });

      if (response.ok) {
        const result = await response.json();

        if (result.success) {
          setVerificationData(result.result);
          setStepOneError("");

          // Prefill if present
          if (result.result?.marketingContactName)
            setMarketingContactName(result.result.marketingContactName);
          if (result.result?.marketingContactPhone)
            setMarketingContactPhone(result.result.marketingContactPhone);
          if (result.result?.marketingContactEmail)
            setMarketingContactEmail(result.result.marketingContactEmail);

          setStepOneCompleted(true);
          announceStepChange(
            "Step 1 complete. Now showing Step 2: Account Information and Step 3: Company Information",
          );
        } else {
          setStepOneError(
            result.errorMessage ||
              "Unable to process your information. Please try again or contact support.",
          );
        }
      } else {
        const errorData = await response.json().catch(() => ({}));
        setStepOneError(
          errorData.message ||
            "Unable to process your information. Please try again or contact support.",
        );
      }
    } catch (err) {
      console.error("Step completion error:", err);
      setStepOneError("An error occurred while processing. Please try again.");
    } finally {
      setIsProcessing(false);
    }
  };

  // ----------------------------
  // Submit (Steps 2 & 3)
  // ----------------------------
  const handleFormSubmit = async e => {
    e.preventDefault();

    if (!googleSiteKey) {
      console.error("Google reCAPTCHA site key is missing");
      return;
    }

    // Skip agency_id and underwriter validation for Stewart employees
    const skipFields = isStewartEmployee ? ["agency_id", "underwriter"] : [];
    const { errors: nextErrors } = validateForm(e.target, skipFields);
    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      const firstKey = Object.keys(nextErrors)[0];
      scrollToError(fieldIdFor(firstKey));
      return;
    }

    setIsSubmitting(true);
    setSubmitError("");
    setSubmitSuccess(false);
    setStepOneError("");

    try {
      // Execute reCAPTCHA
      const token = await new Promise((resolve, reject) => {
        if (window.grecaptcha && window.grecaptcha.ready) {
          window.grecaptcha.ready(() => {
            window.grecaptcha
              .execute(googleSiteKey, { action: "registration" })
              .then(resolve)
              .catch(reject);
          });
        } else {
          reject(new Error("reCAPTCHA not loaded"));
        }
      });

      const formData = new FormData(e.target);
      const payload = {
        UserName: email, // disabled/readOnly input
        Password: formData.get("password"),
        FirstName: formData.get("first_name"),
        LastName: formData.get("last_name"),
        EmailAddress: email,
        AgencyId: isStewartEmployee ? "" : agencyId || "",
        Underwriter: isStewartEmployee ? "" : underwriter || "",
        companyName: formData.get("companyname"),
        streetAddress1: formData.get("streetaddress1"),
        streetAddress2: formData.get("streetaddress2"),
        City: formData.get("city"),
        addressState: formData.get("addressstate"),
        zipCode: formData.get("zip"),
        Country: formData.get("country"),
        phoneNumber: formData.get("phone"),
        Fax: formData.get("fax"),
        JobTitle: formData.get("job_title"),
        MarketingContact: {
          Name: formData.get("marketing_contact_name"),
          Phone: formData.get("marketing_contact_phone"),
          Email: formData.get("marketing_contact_email"),
        },
        UserType: verificationData?.userType || "",
        GoogleCaptchaToken: token,
        DatasourceId: formData.get("datasource_id"),
        HowDidYouHearAboutUs: selectedHowDidYouHear,
        HowDidYouHearAboutUsOtherText: formData.get("how-did-you-hear-other"),
      };

      const response = await fetch("/api/Account/RegisterUser", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setSubmitSuccess(true);
        setErrors({});

        setTimeout(() => {
          const successElement = document.querySelector(
            ".registration-form__success",
          );
          if (successElement) {
            successElement.scrollIntoView({
              behavior: "smooth",
              block: "center",
            });
          }
        }, 100);
      } else {
        let errorMsg =
          result.errorMessage ||
          result.message ||
          "Registration failed. Please try again.";
        let parsed = {};
        try {
          // Try to parse errorMessage as JSON
          if (
            typeof errorMsg === "string" &&
            errorMsg.startsWith("{") &&
            errorMsg.endsWith("}")
          ) {
            parsed = JSON.parse(errorMsg);
          }
        } catch (e) {
          // Not JSON, fallback to string
        }
        if (Object.keys(parsed).length) {
          // Set field errors
          setErrors(prev => ({ ...prev, ...parsed }));
          // Scroll to first error field
          const firstKey = Object.keys(parsed)[0];
          scrollToError(fieldIdFor(firstKey));
        } else {
          setSubmitError(errorMsg);
        }
      }
    } catch (error) {
      console.error("Registration error:", error);
      setSubmitError(
        "An error occurred during registration. Please try again.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="registration-form">
      {/* Screen reader announcements */}
      <div
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
        role="status"
      >
        {announcement}
      </div>

      <form
        className="registration-form__form"
        noValidate
        onSubmit={handleFormSubmit}
        aria-label="Multi-step registration form"
      >
        {/* Step 1 */}
        <fieldset
          className="registration-form__step"
          id="step-1"
          aria-describedby="step-1-description"
        >
          <legend className="registration-form__title">
            Step 1 of 3: Member verification
          </legend>

          <p className="registration-form__description" id="step-1-description">
            Please type in your current email address. We will use this to
            validate if you are already in our system.
          </p>

          {stepOneError && (
            <div className="registration-form__error form-error" role="alert">
              {stepOneError}
            </div>
          )}

          <div className="registration-form__field registration-form__field--required">
            <label htmlFor="email" className="registration-form__label">
              Email address{" "}
              <span className="registration-form__required" aria-hidden="true">
                *
              </span>
            </label>
            <input
              type="email"
              id="email"
              name="email"
              className={`registration-form__input ${errors.email ? "registration-form__input--error" : ""}`}
              value={email}
              onChange={handleEmailChange}
              onKeyDown={handleEmailKeyDown}
              required
              autoComplete="email"
              aria-describedby={`step-1-description ${errors.email ? "email-error" : ""}`.trim()}
              aria-invalid={errors.email ? "true" : "false"}
              disabled={isVerifying || isProcessing}
            />
            <div
              id="email-error"
              className="registration-form__error"
              hidden={!errors.email}
            >
              {errors.email}
            </div>
          </div>

          {/* After email verified and NOT Stewart employees */}
          {stepOneVerified && !isStewartEmployee && (
            <>
              <div className="registration-form__field registration-form__field--required">
                <label htmlFor="agency-id" className="registration-form__label">
                  Agency ID{" "}
                  <span
                    className="registration-form__required"
                    aria-hidden="true"
                  >
                    *
                  </span>
                </label>
                <input
                  type="text"
                  id="agency-id"
                  name="agency_id"
                  className={`registration-form__input ${errors.agency_id ? "registration-form__input--error" : ""}`}
                  value={agencyId}
                  onChange={handleAgencyIdChange}
                  required
                  aria-describedby={`agency-id-hint ${errors.agency_id ? "agency-id-error" : ""}`.trim()}
                  aria-invalid={errors.agency_id ? "true" : "false"}
                  disabled={isProcessing}
                />
                <div id="agency-id-hint" className="registration-form__hint">
                  Enter your assigned Agency ID.
                </div>
                <div
                  id="agency-id-error"
                  className="registration-form__error"
                  hidden={!errors.agency_id}
                >
                  {errors.agency_id}
                </div>
              </div>

              <div className="registration-form__field registration-form__field--required">
                <label
                  htmlFor="underwriter"
                  className="registration-form__label"
                >
                  Underwriter{" "}
                  <span
                    className="registration-form__required"
                    aria-hidden="true"
                  >
                    *
                  </span>
                </label>
                <select
                  id="underwriter"
                  name="underwriter"
                  className={`registration-form__select ${errors.underwriter ? "registration-form__select--error" : ""}`}
                  value={underwriter}
                  onChange={handleUnderwriterChange}
                  required
                  aria-describedby={
                    errors.underwriter ? "underwriter-error" : ""
                  }
                  aria-invalid={errors.underwriter ? "true" : "false"}
                  disabled={isProcessing}
                >
                  <option value="" disabled>
                    — Select an underwriter —
                  </option>
                  {underwriterOptions &&
                    underwriterOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.name}
                      </option>
                    ))}
                </select>
                <div
                  id="underwriter-error"
                  className="registration-form__error"
                  hidden={!errors.underwriter}
                >
                  {errors.underwriter}
                </div>
              </div>
            </>
          )}

          <div className="registration-form__submit">
            {!stepOneVerified ? (
              <button
                type="button"
                className="registration-form__button btn--primary-large"
                id="verify-btn"
                onClick={handleVerifyAccount}
                disabled={isVerifying}
              >
                {isVerifying ? "Verifying..." : "Verify your account"}
              </button>
            ) : !isStewartEmployee ? (
              <button
                type="button"
                className="registration-form__button btn--primary-large"
                id="complete-step-one-btn"
                onClick={handleCompleteStepOne}
                disabled={isProcessing}
              >
                {isProcessing ? "Processing..." : "Verify your account"}
              </button>
            ) : null}
          </div>
        </fieldset>

        {/* Step 2 - Use component */}
        <Step2
          firstName={firstName}
          lastName={lastName}
          email={email}
          errors={errors}
          handleFirstNameChange={handleFirstNameChange}
          handleLastNameChange={handleLastNameChange}
          clearFieldError={clearFieldError}
          isVisible={stepOneCompleted}
          stepNumber={2}
          totalSteps={3}
          hasTitle={true}
        />

        {/* Step 3 - Use component */}
        <Step3
          countries={countries}
          states={states}
          underwriterOptions={underwriterOptions}
          howDidYouHearItems={howDidYouHearItems}
          agencyId={agencyId}
          underwriter={underwriter}
          verificationData={verificationData}
          selectedHowDidYouHear={selectedHowDidYouHear}
          howDidYouHearOther={howDidYouHearOther}
          marketingContactName={marketingContactName}
          marketingContactPhone={marketingContactPhone}
          marketingContactEmail={marketingContactEmail}
          errors={errors}
          handleCompanyAgencyIdChange={handleCompanyAgencyIdChange}
          handleCompanyUnderwriterChange={handleCompanyUnderwriterChange}
          clearFieldError={clearFieldError}
          handleHowDidYouHearChange={handleHowDidYouHearChange}
          handleHowDidYouHearOtherChange={handleHowDidYouHearOtherChange}
          handleMarketingContactNameChange={handleMarketingContactNameChange}
          handleMarketingContactPhoneChange={handleMarketingContactPhoneChange}
          handleMarketingContactEmailChange={handleMarketingContactEmailChange}
          isVisible={stepOneCompleted}
          stepNumber={3}
          totalSteps={3}
          submitSuccess={submitSuccess}
          submitError={submitError}
          isSubmitting={isSubmitting}
          dataSourceId={dataSourceId}
          googleSiteKey={googleSiteKey}
          hasTitle={true}
          submitButtonText="Create Profile"
          successMessage="Registration Successful!"
          successDescription="Your account has been created successfully. You should receive a confirmation email shortly with further instructions."
          isStewartEmployee={isStewartEmployee}
        />
      </form>
    </div>
  );
};

RegistrationForm.propTypes = propTypes;
export default RegistrationForm;
